import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CallbackPageType } from "@krea/shared/types/common";

import { useAuthStore } from "../../auth/authStore";
import type { BankIDAuthConfig, BankIDError } from "../common-types";
import { authenticateViaFI, finalizeFI, initializeFI } from "./bankid-fi";
import type { FIBankIDState } from "./types";

const FI_BANKID_QUERY_KEY = "fi-bankid";

// User cancellation error codes
const USER_CANCELLATION_ERRORS = ["AUS-3200", "IDP-3200", "cancel"];

interface UseFIBankIDConfig extends BankIDAuthConfig {
  extraData?: {
    preferredLanguage?: string | null;
    gaClientId?: string | null;
  };
}

export const useFIBankID = (config: UseFIBankIDConfig) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    extraData,
  } = config;

  const redirectUrl = `${window.location.origin}/callback-page/${CallbackPageType.FI_BANK_ID}`;

  const [state, setState] = useState<FIBankIDState>({ status: "idle" });
  const isMountedRef = useRef(true);
  const popupRef = useRef<Window | null>(null);
  const { setAccessToken, setRefreshToken } = useAuthStore();

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Initialize query - fetch list of banks
  const initializeQuery = useQuery({
    queryKey: [FI_BANKID_QUERY_KEY, "initialize"],
    queryFn: async () => {
      const response = await initializeFI();

      return response.data;
    },
    enabled: state.status === "idle",
    staleTime: Number.POSITIVE_INFINITY,
    retry: 3,
  });

  // Handle initialize query state changes
  useEffect(() => {
    if (isMountedRef.current && initializeQuery.data && state.status === "idle")
      setState({ status: "authenticating" });
  }, [initializeQuery.data, state.status]);

  // Handle initialize query errors
  useEffect(() => {
    if (!isMountedRef.current || !initializeQuery.error) return;

    console.error("Failed to initialize FI BankID:", initializeQuery.error);
    Sentry.captureException(initializeQuery.error);

    const error: BankIDError = {
      code: "INIT_FAILED",
      message: t("bankID.errors.genericError"),
    };

    setState({ status: "error", error });

    if (onError) {
      onError(error);
    }
  }, [initializeQuery.error, onError, t]);

  // Authenticate mutation - start OAuth flow
  const authenticateMutation = useMutation({
    mutationFn: async (ftnIdpId: string) => {
      const response = await authenticateViaFI({
        ftn_idp_id: ftnIdpId,
        redirect_url: redirectUrl,
        ui_locales: language,
      });

      return response.data;
    },
    onSuccess: (data) => {
      if (!isMountedRef.current) {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }

        return;
      }

      if (!data.authUrl) {
        throw new Error("No authUrl in response");
      }

      if (popupRef.current) {
        popupRef.current.location.href = data.authUrl;
        popupRef.current.focus();
        setState({ status: "pending" });
      }
    },
    onError: (err) => {
      if (!isMountedRef.current) return;

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }

      console.error("Failed to start FI OAuth:", err);
      Sentry.captureException(err);

      const error: BankIDError = {
        code: "OAUTH_START_FAILED",
        message: t("bankID.errors.startFailed"),
      };

      setState({ status: "error", error });

      if (onError) {
        onError(error);
      }
    },
  });

  // Finalize mutation - exchange code for tokens
  const finalizeMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await finalizeFI({
        code,
        preferred_language: extraData?.preferredLanguage ?? language,
        redirect_url: redirectUrl,
        authentication_context: authenticationContext,
        identification_context: identificationContext ?? undefined,
        extra_data: extraData
          ? {
              preferred_language: extraData.preferredLanguage,
              ga_client_id: extraData.gaClientId,
            }
          : undefined,
      });

      return response.data;
    },
    onSuccess: (data) => {
      if (!isMountedRef.current) return;

      setState({ status: "success" });

      // ADMIN_WEB gets a Django session cookie and no tokens at all, so there is
      // nothing to persist — storing tokens is what the admin app must not do.
      const tokens = data.tokens
        ? {
            accessToken: data.tokens.accessToken,
            refreshToken: data.tokens.refreshToken,
          }
        : null;

      if (tokens) {
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);
      }

      onSuccess(tokens);
    },
    onError: (err) => {
      if (!isMountedRef.current) return;

      console.error("FI BankID finalize failed:", err);
      Sentry.captureException(err);

      const error: BankIDError = {
        code: "FINALIZE_FAILED",
        message: t("bankID.errors.genericError"),
      };

      setState({ status: "error", error });

      if (onError) {
        onError(error);
      }
    },
  });

  // Listen for OAuth callback via BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel(CallbackPageType.FI_BANK_ID);

    channel.onmessage = async (
      event: MessageEvent<{ code?: string; error?: string }>,
    ) => {
      if (!isMountedRef.current) return;

      const { code, error: errorCode } = event.data;

      // Handle cancel
      if (code === "cancel" || errorCode === "cancel") {
        const error: BankIDError = {
          code: "USER_CANCELLED",
          message: t("bankID.errors.userCancelled"),
          isUserCancellation: true,
        };

        setState({ status: "error", error });

        if (onCancel) onCancel();

        return;
      }

      // Handle error
      if (errorCode) {
        const isUserCancellation = USER_CANCELLATION_ERRORS.includes(errorCode);

        if (isUserCancellation) {
          const error: BankIDError = {
            code: "USER_CANCELLED",
            message: t("bankID.errors.userCancelled"),
            isUserCancellation: true,
          };

          setState({ status: "error", error });

          if (onCancel) onCancel();
        } else {
          Sentry.captureMessage(`FI BankID error code: ${errorCode}`);

          const error: BankIDError = {
            code: "AUTHENTICATION_FAILED",
            message: t("bankID.errors.genericError"),
          };

          setState({ status: "error", error });

          if (onError) {
            onError(error);
          }
        }

        return;
      }

      // Handle success with code
      if (code) {
        finalizeMutation.mutate(code);
      }
    };

    return () => channel.close();
  }, [finalizeMutation, onCancel, onError, t]);

  // Note: Initialize query runs automatically when state.status === "idle" (via enabled flag)

  // Memoized identity providers from query
  const identityProviders = useMemo(
    () => initializeQuery.data?.identityProviders ?? [],
    [initializeQuery.data],
  );

  // Select bank and start OAuth flow
  const selectBank = useCallback(
    (ftnIdpId: string) => {
      // Determine if mobile (use new tab) or desktop (use popup)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      let popup: Window | null = null;

      if (isMobile) {
        popup = window.open("about:blank", "_blank");
      } else {
        const windowHeight = 950;
        const windowWidth = 600;
        const features = `location=no,menubar=no,status=no,titlebar=no,toolbar=no,width=${windowWidth},height=${windowHeight},top=${(window.innerHeight - windowHeight) / 2},left=${(window.innerWidth - windowWidth) / 2}`;

        popup = window.open("about:blank", "FI_BankID_Popup", features);
      }

      if (!popup) {
        console.error("Failed to open popup - likely blocked by browser");
        Sentry.captureMessage("FI BankID popup was blocked by browser");

        const error: BankIDError = {
          code: "POPUP_BLOCKED",
          message: t("bankID.errors.popupBlocked"),
        };

        setState({ status: "error", error });

        if (onError) {
          onError(error);
        }

        return;
      }

      popupRef.current = popup;
      authenticateMutation.mutate(ftnIdpId);
    },
    [authenticateMutation, onError, t],
  );

  // Cancel/reset
  const cancel = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    queryClient.cancelQueries({ queryKey: [FI_BANKID_QUERY_KEY] });
    setState({ status: "idle" });

    if (onCancel) onCancel();
  }, [queryClient, onCancel]);

  // Reset
  const reset = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    queryClient.cancelQueries({ queryKey: [FI_BANKID_QUERY_KEY] });
    setState({ status: "idle" });
  }, [queryClient]);

  return {
    state,
    identityProviders,
    selectBank,
    cancel,
    reset,
  };
};
